export const TRAIN_LAB_CLASSES = [
  "cat",
  "dog",
  "wolf",
  "lion",
  "tiger",
  "fox",
  "leopard",
  "bear",
];

export const TRAIN_LAB_NAMES = {
  cat: "Cat",
  dog: "Dog",
  wolf: "Wolf",
  lion: "Lion",
  tiger: "Tiger",
  fox: "Fox",
  leopard: "Leopard",
  bear: "Bear",
};

// Educational toy features, not pixels from the emoji/image:
// [size, pointy ears, long snout, mane, stripes, spots]
export const TRAIN_LAB_PETS = [
  ["🐈", "cat", [0.2, 0.9, 0.2, 0, 0, 0]],
  ["🐱", "cat", [0.15, 0.9, 0.15, 0, 0, 0]],
  ["🐈‍⬛", "cat", [0.25, 0.85, 0.2, 0, 0.2, 0]],
  ["😺", "cat", [0.2, 0.95, 0.25, 0, 0.1, 0]],
  ["🐕", "dog", [0.5, 0.4, 0.8, 0, 0, 0]],
  ["🐶", "dog", [0.3, 0.5, 0.6, 0, 0, 0]],
  ["🦮", "dog", [0.6, 0.3, 0.85, 0, 0, 0]],
  ["🐩", "dog", [0.45, 0.2, 0.6, 0, 0, 0.1]],
];

export const TRAIN_LAB_WILD = [
  ["🐺", "wolf", [0.8, 0.85, 0.9, 0, 0, 0]],
  ["🐺", "wolf", [0.85, 0.9, 0.85, 0, 0, 0]],
  ["🐺", "wolf", [0.75, 0.8, 0.95, 0, 0, 0]],
  ["🦁", "lion", [0.9, 0.3, 0.5, 0.9, 0, 0]],
  ["🦁", "lion", [0.95, 0.35, 0.55, 0.85, 0, 0]],
  ["🐅", "tiger", [0.9, 0.4, 0.5, 0, 0.9, 0]],
  ["🐅", "tiger", [0.85, 0.45, 0.55, 0, 0.95, 0]],
  ["🦊", "fox", [0.4, 0.9, 0.8, 0, 0, 0]],
  ["🦊", "fox", [0.35, 0.95, 0.75, 0, 0, 0]],
  ["🦊", "fox", [0.45, 0.9, 0.85, 0, 0, 0]],
  ["🐆", "leopard", [0.7, 0.4, 0.5, 0, 0, 0.9]],
  ["🐆", "leopard", [0.65, 0.45, 0.55, 0, 0, 0.95]],
  ["🐻", "bear", [0.95, 0.3, 0.6, 0, 0, 0]],
  ["🐻", "bear", [1, 0.25, 0.55, 0, 0, 0]],
];

// Eight examples, one from each class. This deliberately matches the pets-only
// dataset's example count so the "narrow vs wider" comparison changes class
// coverage without also changing the number of examples.
export const TRAIN_LAB_WIDE = [
  TRAIN_LAB_PETS[0],   // cat
  TRAIN_LAB_PETS[4],   // dog
  TRAIN_LAB_WILD[0],   // wolf
  TRAIN_LAB_WILD[3],   // lion
  TRAIN_LAB_WILD[5],   // tiger
  TRAIN_LAB_WILD[7],   // fox
  TRAIN_LAB_WILD[10],  // leopard
  TRAIN_LAB_WILD[12],  // bear
];

// Held-out toy test cases. None of these six feature vectors is identical to a
// training vector above. They test the learned decision boundaries rather than
// simply replaying a training example.
export const TRAIN_LAB_TESTS = [
  { icon: "🐈", label: "cat", x: [0.18, 0.88, 0.18, 0, 0.05, 0], name: "House cat" },
  { icon: "🐕", label: "dog", x: [0.48, 0.35, 0.76, 0, 0, 0.04], name: "Dog" },
  { icon: "🐺", label: "wolf", x: [0.82, 0.87, 0.88, 0, 0, 0], name: "Wolf" },
  { icon: "🦁", label: "lion", x: [0.92, 0.33, 0.52, 0.88, 0, 0], name: "Lion" },
  { icon: "🦊", label: "fox", x: [0.42, 0.92, 0.8, 0, 0, 0], name: "Fox" },
  { icon: "🐅", label: "tiger", x: [0.88, 0.42, 0.53, 0, 0.92, 0], name: "Tiger" },
];

export const TRAIN_LAB_BRIEF_STEPS = 3;
export const TRAIN_LAB_LONG_STEPS = 250;
export const TRAIN_LAB_LEARNING_RATE = 3;

function trainSoftmax(data, steps) {
  const classCount = TRAIN_LAB_CLASSES.length;
  const featureCount = 6;
  const weights = Array.from({ length: classCount }, () => Array(featureCount).fill(0));
  const bias = Array(classCount).fill(0);

  const probabilities = (x) => {
    const logits = weights.map((row, k) =>
      row.reduce((sum, weight, j) => sum + weight * x[j], bias[k])
    );
    const maxLogit = Math.max(...logits);
    const exps = logits.map((value) => Math.exp(value - maxLogit));
    const total = exps.reduce((sum, value) => sum + value, 0);
    return exps.map((value) => value / total);
  };

  for (let step = 0; step < steps; step += 1) {
    const gradW = Array.from({ length: classCount }, () => Array(featureCount).fill(0));
    const gradB = Array(classCount).fill(0);

    for (const [, label, x] of data) {
      const probs = probabilities(x);
      const target = TRAIN_LAB_CLASSES.indexOf(label);
      for (let k = 0; k < classCount; k += 1) {
        const delta = probs[k] - (k === target ? 1 : 0);
        gradB[k] += delta;
        for (let j = 0; j < featureCount; j += 1) {
          gradW[k][j] += delta * x[j];
        }
      }
    }

    const count = data.length;
    for (let k = 0; k < classCount; k += 1) {
      bias[k] -= TRAIN_LAB_LEARNING_RATE * gradB[k] / count;
      for (let j = 0; j < featureCount; j += 1) {
        weights[k][j] -= TRAIN_LAB_LEARNING_RATE * gradW[k][j] / count;
      }
    }
  }

  return probabilities;
}

export function buildTrainLabVariants() {
  const narrowData = TRAIN_LAB_PETS;
  const wideData = TRAIN_LAB_WIDE;

  return [
    {
      key: "narrow-brief",
      dataLabel: "Pets-only data",
      trainingLabel: "Brief training",
      examples: narrowData.length,
      animalTypes: 2,
      steps: TRAIN_LAB_BRIEF_STEPS,
      model: trainSoftmax(narrowData, TRAIN_LAB_BRIEF_STEPS),
    },
    {
      key: "narrow-long",
      dataLabel: "Pets-only data",
      trainingLabel: "Longer training",
      examples: narrowData.length,
      animalTypes: 2,
      steps: TRAIN_LAB_LONG_STEPS,
      model: trainSoftmax(narrowData, TRAIN_LAB_LONG_STEPS),
    },
    {
      key: "wide-brief",
      dataLabel: "Wider animal data",
      trainingLabel: "Brief training",
      examples: wideData.length,
      animalTypes: 8,
      steps: TRAIN_LAB_BRIEF_STEPS,
      model: trainSoftmax(wideData, TRAIN_LAB_BRIEF_STEPS),
    },
    {
      key: "wide-long",
      dataLabel: "Wider animal data",
      trainingLabel: "Longer training",
      examples: wideData.length,
      animalTypes: 8,
      steps: TRAIN_LAB_LONG_STEPS,
      model: trainSoftmax(wideData, TRAIN_LAB_LONG_STEPS),
    },
  ];
}

export function classifyTrainLab(variant, test) {
  const probs = variant.model(test.x);
  const max = Math.max(...probs);
  const index = probs.indexOf(max);
  return {
    label: TRAIN_LAB_CLASSES[index],
    name: TRAIN_LAB_NAMES[TRAIN_LAB_CLASSES[index]],
    modelScore: Math.round(max * 100),
    correct: TRAIN_LAB_CLASSES[index] === test.label,
  };
}
